import { useState, useEffect } from "react";
import PanelDrawer from "../components/panel/panel-drawer";
import PanelHeader from "../components/panel/panel-header";
import PanelTable from "../components/panel/panel-table";
import { Panel } from "../types/panel.types";
import { useCreatePanelMutation, useUpdatePanelFieldMutation, useUpdatePanelMutation, useGenerateSignedUploadUrlMutation, useSetPanelAudienceMutation } from "../endpoints/panel.endpoints";
import notificationService from "src/services/notification/notification.service";
import { useDispatch } from "react-redux";
import UploadStatusBar, { UploadStatus } from "src/components/upload-status-bar/upload-status-bar";
import { limitCSVToRows } from "../utils/csv-utils";

const PanelsTab = () => {
  const [open, setOpen] = useState(false);
  const [panel, setPanel] = useState<Panel | undefined>(undefined);
  const [activePanel, setActivePanel] = useState<Panel | null>(null);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus | null>(null);
  const dispatch = useDispatch();

  const [createPanel] = useCreatePanelMutation();
  const [updatePanel] = useUpdatePanelMutation();
  const [updatePanelField] = useUpdatePanelFieldMutation();
  const [setPanelAudience] = useSetPanelAudienceMutation();
  const [generateSignedUploadUrl] = useGenerateSignedUploadUrlMutation();

  useEffect(() => {
    if (panel) {
      setActivePanel(panel);
    } else {
      setActivePanel(null);
    }
  }, [panel]);

  const handleOpenChange = (panelOrOpen?: Panel | boolean) => {
    if (panelOrOpen === false) {
      setOpen(false);
      setPanel(undefined);
      return;
    }
    setPanel(panelOrOpen as Panel | undefined);
    setOpen((prev) => !prev);
  };

  const uploadFileToS3 = async(file: File, signedUrl: string): Promise<void> => {
    if (typeof signedUrl !== 'string' || !signedUrl) {
      throw new Error('Invalid signed URL received from server');
    }

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percent = Math.round((e.loaded * 100) / e.total);
          const mappedProgress = 30 + (percent * 0.7);
          setUploadStatus({
            fileName: file.name,
            progress: mappedProgress,
            status: 'uploading',
          });
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          setUploadStatus({
            fileName: file.name,
            progress: 100,
            status: 'success',
          });
          resolve();
        } else {
          const errorText = xhr.responseText || `Upload failed with status ${xhr.status}`;
          let errorMessage = errorText;

          if (errorText.includes('<Code>')) {
            const codeMatch = errorText.match(/<Code>([^<]+)<\/Code>/);
            const messageMatch = errorText.match(/<Message>([^<]+)<\/Message>/);
            if (codeMatch && messageMatch) {
              errorMessage = `${codeMatch[1]}: ${messageMatch[1]}`;
            }
          }

          setUploadStatus({
            fileName: file.name,
            progress: 0,
            status: 'error',
            error: errorMessage,
          });
          reject(new Error(errorMessage));
        }
      });

      xhr.addEventListener('error', () => {
        setUploadStatus({
          fileName: file.name,
          progress: 0,
          status: 'error',
          error: 'Network error during upload',
        });
        reject(new Error('Network error during upload'));
      });

      xhr.open('PUT', signedUrl);
      xhr.setRequestHeader('Content-Type', 'text/csv');
      xhr.send(file);
    });
  };

  const handleCreatePanelFromFile = async(file: File) => {
    try {
      if (!file) return;

      setUploadStatus({
        fileName: file.name,
        progress: 0,
        status: 'uploading',
      });

      setUploadStatus({
        fileName: file.name,
        progress: 5,
        status: 'uploading',
      });

      const limitedFile = await limitCSVToRows(file, 500);

      setUploadStatus({
        fileName: file.name,
        progress: 10,
        status: 'uploading',
      });

      const createdPanel = await createPanel({
        name: file?.name.split('.')[0] ?? 'Untitled Panel',
        file: limitedFile,
        client: undefined,
      }).unwrap();

      setUploadStatus({
        fileName: file.name,
        progress: 20,
        status: 'uploading',
      });

      let signedUrl: string;
      try {
        signedUrl = await generateSignedUploadUrl({
          panel_id: createdPanel.id,
          filename: limitedFile.name,
        }).unwrap();
      } catch (error) {
        console.error('Error generating signed URL:', error);
        throw new Error(`Failed to generate signed URL: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }

      setUploadStatus({
        fileName: file.name,
        progress: 30,
        status: 'uploading',
      });

      await uploadFileToS3(file, signedUrl);

      return createdPanel;
    } catch (error) {
      setUploadStatus({
        fileName: file.name,
        progress: 0,
        status: 'error',
        error: error instanceof Error ? error.message : 'Failed to upload file',
      });
      notificationService.handleError(error, dispatch, 'Failed to create panel');
      console.error('Failed to create panel:', error);
      throw error;
    }
  };

  const handleFileChange = async(file: File | null) => {
    if (!file) return;
    try {
      const createdPanel = await handleCreatePanelFromFile(file);
      if (createdPanel) {
        setActivePanel(createdPanel);
      }
    } catch (error) {
      // Error handling is done in handleCreatePanelFromFile
    }
  };

  const handlePanelChange = (field: string, value: any) => {
    try {
      if (!activePanel) return;
      if (field === 'organizations' || field === 'users') {
        setActivePanel(prev => ({
          ...prev!,
          [field]: value,
        }));
        return;
      }
      void updatePanel({ id: activePanel?.id ?? '', field, value });
      setActivePanel(prev => ({
        ...prev!,
        [field]: value,
      }));
    } catch (error) {
      notificationService.handleError(error, dispatch, 'Failed to update panel');
      console.error('Failed to update panel:', error);
    }
  };

  const handleAudienceBlur = (assignmentType: 'organizations' | 'users') => {
    if (!activePanel?.id) return;
    const organizationIds = activePanel.organizations?.map((o) => o.id) ?? [];
    const rawUserIds = activePanel.users?.map((u) => (typeof u === "string" ? u : u.id)) ?? [];
    const userIds = rawUserIds.filter((id): id is string => Boolean(id) && !String(id).includes("@"));
    try {
      void setPanelAudience({ panelId: activePanel.id, organizationIds, userIds, assignmentType });
    } catch (error) {
      notificationService.handleError(error, dispatch, 'Failed to update panel audience');
    }
  };

  const handleColumnsChange = (id: string, field: string, value: any) => {
    try {
      void updatePanelField({ id, field, value });

      const updatedFields = (activePanel?.fields ?? []).map(fieldObj => {
        if (fieldObj.id === id) {
          return {
            ...fieldObj,
            [field]: value,
          };
        }
        return fieldObj;
      });

      setActivePanel(prev => ({
        ...prev!,
        fields: updatedFields,
      }));
    } catch (error) {
      notificationService.handleError(error, dispatch, 'Failed to update panel fields');
      console.error('Failed to update panel fields:', error);
    }
  };

  const handleUnlock = async(panelId: string) => {
    try {
      await updatePanel({ id: panelId, field: 'unlock', value: true }).unwrap();
      setActivePanel(prev => (prev ? { ...prev, locked: false } : null));
    } catch (error) {
      notificationService.handleError(error, dispatch, 'Failed to unlock panel');
    }
  };

  const handleLock = async(panelId: string) => {
    try {
      await updatePanel({ id: panelId, field: 'locked', value: true }).unwrap();
      setActivePanel(prev => (prev ? { ...prev, locked: true } : null));
    } catch (error) {
      notificationService.handleError(error, dispatch, 'Failed to lock panel');
    }
  };

  return (
    <div className="">
      <PanelDrawer
        open={open}
        panel={panel}
        activePanel={activePanel}
        onOpenChange={handleOpenChange}
        onFileChange={handleFileChange}
        onPanelChange={handlePanelChange}
        onColumnsChange={handleColumnsChange}
        onUnlock={handleUnlock}
        onLock={handleLock}
        onAudienceBlur={handleAudienceBlur}
      />
      <PanelHeader />
      <PanelTable
        onOpenChange={handleOpenChange}
      />
      <UploadStatusBar
        uploadStatus={uploadStatus}
        onDismiss={() => setUploadStatus(null)}
      />
    </div>
  );
};

export default PanelsTab;
